import {
  Button,
  Card,
  Col,
  Input,
  message,
  Row,
  Space,
  Table,
  Tooltip,
} from "antd";
import confirm from "antd/lib/modal/confirm";
import { deleteAgent, getAgents } from "api/agents";
import { TABLE_LIMIT } from "helpers/consts";
import BreadCrumbs from "layout/components/content/breadcrumbs";
import { useEffect, useState } from "react";
import { Delete, Edit } from "react-iconly";
import { RiCheckboxCircleLine, RiInformationLine } from "react-icons/ri";
import { useHistory, useLocation } from "react-router-dom";

export default function Agents() {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );

  const [searchTerm, setSearchTerm] = useState("");

  const history = useHistory();

  const fetchData = async () => {
    setLoading(true);

    const offset = (currentPage - 1) * TABLE_LIMIT;

    try {
      const response = await getAgents({
        limit: TABLE_LIMIT,
        offset,
      });
      setData(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const handleNewAgent = () => {
    history.push("/agents/create");
  };

  const showPromiseConfirm = (title: string, uuid: string) => {
    confirm({
      title: (
        <h5 className="hp-mb-0 hp-font-weight-500">
          {`Do you want to delete the ${title} agent?`}
        </h5>
      ),
      icon: (
        <span className="remix-icon">
          <RiInformationLine color="red" />
        </span>
      ),
      onOk: async () => {
        try {
          await deleteAgent({ agentId: uuid });
          message.success({
            content: "Agent deleted successfully!",
            icon: <RiCheckboxCircleLine className="remix-icon" />,
            type: "success",
            duration: 3,
          });
          fetchData();
        } catch (error) {
          console.error(error);
        }
      },
      onCancel() {
        return null;
      },
    });
  };

  const handlePageChange = (page) => {
    searchParams.set("page", page);
    history.push(`?${searchParams.toString()}`);
    setCurrentPage(page);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Phone Numbers",
      dataIndex: "phoneNumbers",
      render: (row) => {
        const phones = row.map(
          (phone) =>
            `${phone.number} - ${phone.enabled ? "enabled" : "disabled"}`
        );

        const list = (
          <ul style={{ listStyle: "none", marginBottom: 0, padding: 0 }}>
            {phones.map((phone) => (
              <li key={phone}>{phone}</li>
            ))}
          </ul>
        );
        return (
          !!phones.length && (
            <>
              {phones.length > 5 ? (
                <Tooltip
                  title={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ flex: 1 }}>{list}</span>
                      <Button
                        icon={<i className="ph-light ph-copy-simple" />}
                        onClick={() => navigator.clipboard.writeText(phones)}
                        style={{
                          marginLeft: "10px",
                        }}
                      />
                    </div>
                  }
                  overlayStyle={{
                    maxWidth: "500px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "500",
                      color: "#1890ff",
                      cursor: "pointer",
                    }}
                  >
                    Phone Numbers
                  </span>
                </Tooltip>
              ) : (
                list
              )}
            </>
          )
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      render: (row) => {
        return (
          <Space size="middle">
            <Tooltip title="Edit">
              <div
                className="hp-text-right hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-primary-1"
                onClick={() => history.push(`/agents/${row.id}/flows`)}
              >
                <Edit size={24} />
              </div>
            </Tooltip>
            <Tooltip title="Delete">
              <div
                className="hp-text-right hp-cursor-pointer hp-transition hp-hover-text-color-danger-1 hp-text-color-danger-1"
                onClick={() => showPromiseConfirm(row.name, row.id)}
              >
                <Delete size={24} />
              </div>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div className="hp-mb-32">
        <Row gutter={[32, 32]} justify="space-between">
          <BreadCrumbs breadCrumbActive="Agents" />
          <Col md={15} span={24}>
            <Row justify="end" align="middle" gutter={16}>
              <Col xs={24} md={12} xl={8}>
                <Input
                  placeholder="Search"
                  prefix={
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17.9424 17.0588L14.0306 13.1479C15.1644 11.7867 15.7298 10.0408 15.6091 8.27342C15.4884 6.50604 14.691 4.85321 13.3828 3.65876C12.0745 2.46432 10.3561 1.82024 8.5851 1.86049C6.81406 1.90074 5.12671 2.62223 3.87407 3.87487C2.62143 5.12751 1.89994 6.81486 1.85969 8.5859C1.81944 10.3569 2.46353 12.0753 3.65797 13.3836C4.85241 14.6918 6.50524 15.4892 8.27263 15.6099C10.04 15.7306 11.7859 15.1652 13.1471 14.0314L17.058 17.9432C17.1161 18.0012 17.185 18.0473 17.2609 18.0787C17.3367 18.1101 17.4181 18.1263 17.5002 18.1263C17.5823 18.1263 17.6636 18.1101 17.7395 18.0787C17.8154 18.0473 17.8843 18.0012 17.9424 17.9432C18.0004 17.8851 18.0465 17.8162 18.0779 17.7403C18.1094 17.6644 18.1255 17.5831 18.1255 17.501C18.1255 17.4189 18.1094 17.3375 18.0779 17.2617C18.0465 17.1858 18.0004 17.1169 17.9424 17.0588ZM3.12518 8.75097C3.12518 7.63845 3.45508 6.55092 4.07316 5.62589C4.69124 4.70087 5.56975 3.9799 6.59758 3.55415C7.62542 3.12841 8.75642 3.01702 9.84756 3.23406C10.9387 3.4511 11.941 3.98683 12.7277 4.7735C13.5143 5.56017 14.0501 6.56245 14.2671 7.65359C14.4841 8.74473 14.3727 9.87573 13.947 10.9036C13.5213 11.9314 12.8003 12.8099 11.8753 13.428C10.9502 14.0461 9.8627 14.376 8.75018 14.376C7.25884 14.3743 5.82906 13.7812 4.77453 12.7266C3.72 11.6721 3.12683 10.2423 3.12518 8.75097Z"
                        fill="#636E72"
                      />
                    </svg>
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>

              <Col>
                <Button
                  block
                  className="hp-mt-sm-16"
                  type="primary"
                  onClick={handleNewAgent}
                >
                  Create New Agent
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      <Card className="hp-mb-32" bodyStyle={{ padding: "0 24px" }}>
        <Col className="hp-mt-32 hp-mb-32">
          <Table
            columns={columns}
            dataSource={data}
            scroll={{ x: "max-content" }}
            pagination={{
              current: currentPage,
              pageSize: TABLE_LIMIT,
              total,
              position: ["bottomCenter"],
              onChange: handlePageChange,
              showSizeChanger: false,
            }}
            loading={loading}
          />
        </Col>
      </Card>
    </>
  );
}
